import {FC} from 'react'
import {useIntl} from 'react-intl'
import {Link} from 'react-router-dom'
import {KTSVG} from '../../../../_metronic/helpers'
import {OrderModel} from '../models/OrderModel'
import {ORDER_ACTION_VIEW_MODE, OrderStatus} from '../constants'
import {useAppSelector} from '../../../../setup/redux/hooks'
import {selectSelectedItems, selectSelectedMode, setSelectMode} from '../redux/OrderRedux'
import {useDispatch} from 'react-redux'
import OrderActionBox from './OrderActionBox'
import {orderStatusClassMapping} from '../utils'
import Tooltip from 'react-tooltip-lite'

export enum LayoutMode {
  Masonry = 'masonry',
  List = 'list',
}

interface Props {
  order: OrderModel
  progress: number
  messageId?: string
  isProcessing: boolean
  layoutMode: LayoutMode
  onLayoutChange: (mode: LayoutMode) => void
}

const OrderDetailHeader: FC<Props> = ({
  order,
  progress,
  messageId,
  isProcessing,
  layoutMode,
  onLayoutChange,
}) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const selectedMode = useAppSelector(selectSelectedMode)
  const selectedItems = useAppSelector(selectSelectedItems)

  return (
    <div className='order-header card mb-6'>
      {/* Top row: back button, title, actions */}
      <div className='order-header__top'>
        <div className='order-header__title-group'>
          <Link to='/orders' className='order-header__back btn btn-icon btn-sm btn-light'>
            <i className='bi bi-arrow-left fs-4'></i>
          </Link>
          <div>
            <div className='d-flex align-items-center gap-3 flex-wrap'>
              <h2 className='order-header__name mb-0'>{order.name}</h2>
              <span className='text-muted fs-7 fw-bold'>
                {order.completedItems}/{order.totalItems}{' '}
                {intl.formatMessage({id: 'OrderDetail.header.ORDER_ITEMS'})}
              </span>
            </div>
            <div className='order-header__meta'>
              <span className={`badge ${orderStatusClassMapping(order.status)} fw-bold`}>
                {intl.formatMessage({id: 'ORDER.STATUS.' + order.status})}
              </span>
              <span className='badge badge-light-primary fw-bold'>
                {intl.formatMessage({id: 'SERVICE.SERVICE.' + order.service?.name})}
              </span>
              <span className='badge badge-light-info fw-bold'>
                {intl.formatMessage({id: order.setting?.name ?? 'unknown'})}
              </span>
            </div>
          </div>
        </div>

        <div className='order-header__actions'>
          <OrderActionBox order={order} viewMode={ORDER_ACTION_VIEW_MODE.FULL} />
          {[OrderStatus.COMPLETED, OrderStatus.PENDING, OrderStatus.AI_FAILED].includes(
            order.status
          ) && (
            <Tooltip
              content={intl.formatMessage({id: 'ORDER.ACTIONS.SELECT'})}
              className='tooltips'
            >
              <button
                type='button'
                className={`w-100 btn-sm me-1 p-1 btn btn-icon btn-sm ${selectedMode ? 'btn-primary' : 'btn-light'}`}
                onClick={() => dispatch(setSelectMode(!selectedMode))}
              >
                <KTSVG
                  path='/media/icons/duotune/coding/cod006.svg'
                  className='svg-icon svg-icon-2x'
                />
                {selectedMode ? (
                  <span className='badge badge-light-info fs-7 ms-2'>
                    {selectedItems.length}/{order.totalItems}{' '}
                    {intl.formatMessage({id: 'ORDER.ACTIONS.SELECT'})}
                  </span>
                ) : intl.formatMessage({id: 'ORDER.ACTIONS.SELECT'})}
              </button>
            </Tooltip>
          )}

          <Tooltip
            content={
              layoutMode === LayoutMode.Masonry
                ? intl.formatMessage({id: 'ORDER.LAYOUT.LIST'})
                : intl.formatMessage({id: 'ORDER.LAYOUT.GALLERY'})
            }
            className='tooltips'
          >
            <button
              type='button'
              className='btn btn-icon btn-sm btn-light'
              onClick={() =>
                onLayoutChange(
                  layoutMode === LayoutMode.Masonry ? LayoutMode.List : LayoutMode.Masonry
                )
              }
            >
              <i
                className={`bi fs-4 ${
                  layoutMode === LayoutMode.Masonry ? 'bi-list-ul' : 'bi-grid-3x3-gap'
                }`}
              ></i>
            </button>
          </Tooltip>
        </div>
      </div>

      {/* Progress bar for AI processing */}
      {isProcessing && order.totalItems > 2 && (
        <div className='order-header__progress'>
          <div className='progress h-6px'>
            <div
              className='progress-bar bg-primary ai-progress-bar'
              role='progressbar'
              style={{width: `${progress}%`}}
              aria-valuenow={progress}
              aria-valuemin={0}
              aria-valuemax={100}
            />
          </div>
          <div className='order-header__progress-info'>
            <span className='text-primary fs-8 fw-bold d-flex align-items-center gap-2'>
              <i className='fas fa-cog processing-cog'></i>
              {messageId && (
                <span className='processing-text' key={messageId}>
                  {intl.formatMessage({id: messageId})}
                </span>
              )}
            </span>
            <span className='text-muted fs-8 fw-bold'>{progress}%</span>
          </div>
        </div>
      )}
    </div>
  )
}

export default OrderDetailHeader
